
import React from 'react';
import { CardContent } from '@/components/ui/card';
import { Zap } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { MatchCompatibility } from '@/types';
import { TooltipProvider } from "@/components/ui/tooltip";
import CompatibilityMetric from './CompatibilityMetric';

interface CompatibilityTabContentProps {
  matchScore?: MatchCompatibility;
}

const CompatibilityTabContent: React.FC<CompatibilityTabContentProps> = ({ 
  matchScore 
}) => {
  if (!matchScore?.compatibilityReasons) {
    return (
      <CardContent className="space-y-4">
        <div className="text-center py-4">
          <Zap className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No compatibility data available</p>
        </div>
      </CardContent>
    );
  }
  
  return (
    <CardContent className="space-y-4">
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Why you might match well:</h4>
        
        <ul className="space-y-2">
          {matchScore.compatibilityReasons.map((reason, index) => (
            <li key={index} className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{reason}</span>
            </li>
          ))}
        </ul>
        
        {matchScore.factors.musicalCompatibility && (
          <>
            <Separator />
            
            <div>
              <h4 className="text-sm font-medium mb-3">Musical Compatibility</h4>
              
              <TooltipProvider>
                <div className="space-y-2">
                  <CompatibilityMetric
                    label="Tempo Alignment"
                    value={matchScore.factors.musicalCompatibility.tempoAlignment}
                    color="bg-emerald-500"
                    explanation="How well your preferred tempos match. Higher scores indicate you both tend to create music at similar speeds, suggesting a natural rhythmic compatibility."
                  />
                  
                  <CompatibilityMetric
                    label="Mood Alignment"
                    value={matchScore.factors.musicalCompatibility.moodAlignment}
                    color="bg-blue-500"
                    explanation="How similar your emotional expressions in music are. Higher scores suggest you both create music with similar emotional qualities and vibes."
                  />
                  
                  <CompatibilityMetric
                    label="Audience Overlap"
                    value={matchScore.factors.musicalCompatibility.audienceOverlap}
                    color="bg-violet-500"
                    explanation="How similar your fan bases are likely to be. Based on genre similarity and popularity levels. Higher scores suggest you share many of the same listeners."
                  />
                  
                  <CompatibilityMetric
                    label="Artistic Complementarity"
                    value={matchScore.factors.musicalCompatibility.artisticComplementarity}
                    color="bg-orange-500"
                    explanation="How well your styles complement each other. A high score means you have enough similarities to work together smoothly, but enough differences to create something unique."
                  />
                </div>
              </TooltipProvider>
            </div>
          </>
        )}
      </div>
    </CardContent>
  );
};

export default CompatibilityTabContent;
